<template>
  <div class="account-activity-page">
    <!-- Header toolbar -->
    <div class="activity-toolbar d-flex align-center justify-space-between px-6 py-4">
      <div class="d-flex align-center" style="gap: 12px">
        <!-- Filter dropdown -->
        <div class="filter-wrapper">
          <button
            class="filter-btn"
            @click="showFilterMenu = !showFilterMenu"
            data-test="activity-filter-btn"
          >
            <svg
              class="filter-btn-icon"
              width="18"
              height="12"
              viewBox="0 0 18 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6H14M1.5 1H16.5M6.5 11H11.5"
                stroke="#344054"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Filter
            <span v-if="selectedTypes.length" class="filter-count-badge">{{
              selectedTypes.length
            }}</span>
          </button>

          <!-- Click-outside backdrop -->
          <div v-if="showFilterMenu" class="menu-backdrop" @click="showFilterMenu = false" />

          <div v-if="showFilterMenu" class="filter-menu-card">
            <div class="filter-menu-title">Filter by Object Type</div>
            <hr class="menu-divider" />
            <ul class="filter-list">
              <li
                v-for="type in objectTypes"
                :key="type.value"
                class="filter-option"
                :class="{ 'filter-option--active': isTypeSelected(type.value) }"
              >
                <button
                  type="button"
                  class="filter-option__btn"
                  :aria-pressed="isTypeSelected(type.value)"
                  @click="toggleType(type.value)"
                >
                  <span>{{ type.label }}</span>
                  <svg
                    v-if="isTypeSelected(type.value)"
                    class="check-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 4L6.5 11L2.5 7"
                      stroke="#7f56d9"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </li>
            </ul>
            <hr class="menu-divider" />
            <div class="filter-clear-wrapper">
              <button
                class="filter-clear-btn"
                :disabled="!selectedTypes.length"
                @click="clearFilter"
              >
                Clear filter
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Search input -->
      <div class="search-wrapper" style="max-width: 320px; width: 100%">
        <svg
          class="search-icon"
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.5 16.5L12.5 12.5M14.5 8A6.5 6.5 0 1 1 1.5 8a6.5 6.5 0 0 1 13 0Z"
            stroke="#667085"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search by Employee"
          class="search-input"
          data-test="activity-search-input"
          @input="onSearchInput"
        />
        <button v-if="searchQuery" class="search-clear-btn" @click="onSearchClear">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13M1 1L13 13"
              stroke="#98a2b3"
              stroke-width="1.67"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Activity content -->
    <div class="activity-content px-6 pb-6">
      <!-- Empty state -->
      <div
        v-if="!isLoading && activities.length === 0"
        class="empty-state d-flex flex-column align-center justify-center py-12"
      >
        <img
          src="@components/assets/side-bar/activity.svg"
          width="40"
          height="40"
          class="mb-4"
          style="opacity: 0.3"
          alt=""
        />
        <div class="empty-state-title">No activity found</div>
        <div class="empty-state-subtitle mt-1">
          {{
            searchQuery || selectedTypes.length
              ? "Try adjusting your search or filter."
              : "Activity will appear here when changes are made."
          }}
        </div>
      </div>

      <!-- Grouped activity stream -->
      <div v-else>
        <div
          v-for="(group, groupIndex) in groupedActivities"
          :key="groupIndex"
          class="activity-group"
        >
          <div class="date-divider">
            <span>{{ group.dateLabel }}</span>
          </div>

          <div v-for="item in group.items" :key="item.id" class="activity-card">
            <ActivityStreamCard
              :actor_avatar="item.actor_avatar"
              :actor_name="item.actor_name"
              :object_type="item.object_type"
              :object_descriptor="item.object_descriptor"
              :verb="item.verb"
              :counterpart="item.counterpart"
              :datetime="item.datetime"
              :is_system_entry="item.is_system_entry"
              :is_legacy="item.is_legacy"
              :action_code="item.action_code"
              :subject_link="item.subject_link"
              :subject_name="item.subject_name"
              :subject_id="item.subject_id"
              :subject_label="item.subject_label"
              :counterpart_name="item.counterpart_name"
              :detail_text="item.detail_text"
              :is_system="item.is_system"
              :subject_href="item.subject_href"
              :counterpart_href="item.counterpart_href"
            />
          </div>
        </div>

        <!-- Load more -->
        <div v-if="hasMore && !isLoading" class="load-more-wrapper d-flex justify-center mt-6">
          <button class="load-more-btn" @click="loadMore" data-test="activity-load-more-btn">
            Load more
          </button>
        </div>

        <div v-if="isLoading" class="d-flex justify-center py-6">
          <ExPrimarySpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActivityStreamCard from "@components/ActivityStreamCard/ActivityStreamCard.vue";
import ExPrimarySpinner from "@components/common/ExPrimarySpinner.vue";
import { getAccountActivityStream } from "@/api";
import { transformActivityData, formatDateLabel } from "./AccountActivityHelpers";

const OBJECT_TYPES = [
  { value: "employee", label: "Employee" },
  { value: "position", label: "Position" },
  { value: "role", label: "Role" },
  { value: "responsibility", label: "Responsibility" },
  { value: "responsibility-to-role", label: "Responsibility-to-role" },
];

export default {
  name: "AccountActivityView",

  components: {
    ActivityStreamCard,
    ExPrimarySpinner,
  },

  data() {
    return {
      activities: [],
      nextCursor: null,
      hasMore: false,
      isLoading: false,
      showFilterMenu: false,
      selectedTypes: [],
      searchQuery: "",
      searchDebounceTimer: null,
      accountId: null,
      objectTypes: OBJECT_TYPES,
    };
  },

  computed: {
    groupedActivities() {
      const sorted = [...this.activities].sort((a, b) => b.createdOnMs - a.createdOnMs);

      const groups = {};
      sorted.forEach((item) => {
        const label = formatDateLabel(new Date(item.createdOnMs));
        if (!groups[label]) groups[label] = [];
        groups[label].push(item);
      });

      return Object.keys(groups).map((dateLabel) => ({
        dateLabel,
        items: groups[dateLabel],
      }));
    },
  },

  async mounted() {
    this.accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    await this.fetchActivities({ reset: true });
  },

  methods: {
    async fetchActivities({ reset = false } = {}) {
      if (this.isLoading) return;
      try {
        this.isLoading = true;
        if (reset) {
          this.activities = [];
          this.nextCursor = null;
          this.hasMore = false;
        }

        const { data } = await getAccountActivityStream(this.accountId, {
          cursor: reset ? "" : this.nextCursor || "",
          object_types: this.selectedTypes.join(","),
          search: this.searchQuery,
        });

        if (data && data.has_more != null) {
          this.hasMore = Boolean(data.has_more);
        }
        if (data) {
          this.nextCursor = data.next_cursor || null;
        }

        if (data && data.cards) {
          const transformed = transformActivityData(data.cards);
          this.activities = reset ? transformed : [...this.activities, ...transformed];
        }
      } catch (error) {
        console.error("Failed to load account activity:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async loadMore() {
      await this.fetchActivities({ reset: false });
    },

    isTypeSelected(value) {
      return this.selectedTypes.includes(value);
    },

    toggleType(value) {
      const index = this.selectedTypes.indexOf(value);
      if (index === -1) {
        this.selectedTypes.push(value);
      } else {
        this.selectedTypes.splice(index, 1);
      }
      this.fetchActivities({ reset: true });
    },

    clearFilter() {
      this.selectedTypes = [];
      this.showFilterMenu = false;
      this.fetchActivities({ reset: true });
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchActivities({ reset: true });
      }, 400);
    },

    onSearchClear() {
      this.searchQuery = "";
      this.fetchActivities({ reset: true });
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  },
};
</script>

<style scoped>
.account-activity-page {
  width: 100%;
  min-height: 100vh;
  background-color: #f9fafb;
}

.activity-toolbar {
  background-color: #ffffff;
  border-bottom: 1px solid #eaecf0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.filter-wrapper {
  position: relative;
}

.filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.filter-btn:hover {
  background: #f9fafb;
}

.filter-btn-icon {
  flex-shrink: 0;
}

.filter-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 9px;
  background: #7f56d9;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99;
}

.filter-menu-card {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  min-width: 200px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08);
  border: 1px solid #eaecf0;
}

.filter-menu-title {
  padding: 12px 16px 8px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #475467;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.menu-divider {
  margin: 0;
  border: none;
  border-top: 1px solid #eaecf0;
}

.filter-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.filter-option {
  margin: 0;
}

.filter-option__btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #344054;
  text-align: left;
}

.filter-option:hover {
  background-color: #f9f5ff;
}

.filter-option--active {
  background-color: #f9f5ff;
}

.filter-option__btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.check-icon {
  flex-shrink: 0;
  margin-left: 8px;
}

.filter-clear-wrapper {
  padding: 8px 16px;
}

.filter-clear-btn {
  background: none;
  border: none;
  padding: 0;
  color: #7f56d9;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.filter-clear-btn:disabled {
  color: #d0d5dd;
  cursor: not-allowed;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  pointer-events: none;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  height: 40px;
  padding: 0 36px 0 38px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  color: #344054;
  background: #ffffff;
  outline: none;
}

.search-input::placeholder {
  color: #667085;
}

.search-input:focus {
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px rgba(127, 86, 217, 0.1);
}

.search-clear-btn {
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: #98a2b3;
}

.activity-content {
  padding-top: 24px;
}

.activity-group {
  margin-bottom: 8px;
}

.date-divider {
  display: flex;
  align-items: center;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #475467;
  margin-bottom: 20px;
  margin-top: 8px;
}

.date-divider::before,
.date-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background-color: #eaecf0;
}

.date-divider span {
  padding: 0 12px;
  white-space: nowrap;
}

.activity-card {
  margin-bottom: 8px;
}

.activity-card:last-child {
  margin-bottom: 0;
}

.load-more-btn {
  height: 40px;
  padding: 0 20px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  min-width: 140px;
}

.load-more-btn:hover {
  background: #f9fafb;
}

.empty-state {
  min-height: 400px;
}

.empty-state-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #344054;
}

.empty-state-subtitle {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #475467;
  text-align: center;
  max-width: 320px;
}
</style>
